import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { LeadForm } from "@/components/common/LeadForm";
import { INDUSTRY_DETAILS, INDUSTRY_ICONS } from "@/data/static";
import { ArrowRight, CheckCircle2, Zap, Shield, Users, FileCheck, Building2 } from "lucide-react";

const pillarIcons = [Zap, Shield, Users, FileCheck];

export default function IndustryLanding() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const data = INDUSTRY_DETAILS[slug];
  if (!data) return <Navigate to="/industries" replace />;

  const Icon = INDUSTRY_ICONS[slug] || Building2;
  const title = lang === "ar" ? data.title_ar : data.title_en;
  const subtitle = lang === "ar" ? data.subtitle_ar : data.subtitle_en;
  const pillars = data.pillars_en;
  const scope = data.scope_en;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: data.title_en,
    provider: { "@type": "LocalBusiness", name: COMPANY.brandFull },
    areaServed: ["Dubai", "Abu Dhabi", "Sharjah", "Ajman"],
    description: data.subtitle_en,
  };

  return (
    <div data-testid={`industry-landing-${slug}`}>
      <SEO title={`${data.title_en} | ${COMPANY.brand}`} description={data.subtitle_en} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-30">
          <img src={data.image} alt={data.title_en} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/30" />
        </div>
        <div className="container-orion relative py-20 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
              <Icon className="w-3.5 h-3.5" />
              {data.eyebrow}
            </div>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-7xl tracking-tighter font-medium leading-[1.02]">
              {title}
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">{subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/health-check"
                data-testid={`industry-${slug}-cta-audit`}
                className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight className="w-4 h-4 rtl-flip" />
              </Link>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent(`Hello Orion — I need FM for a ${slug} facility in UAE.`)}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-2xl overflow-hidden">
            {data.benchmarks.map((b, i) => (
              <div key={i} className="bg-slate-950 p-7">
                <div className="font-heading text-2xl lg:text-3xl font-medium tracking-tighter text-blue-400">{b.v}</div>
                <div className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">{b.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow">How we deliver</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Tuned for your operational reality.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map((p, i) => {
              const PIcon = pillarIcons[i] || Zap;
              return (
                <div
                  key={i}
                  data-testid={`industry-${slug}-pillar-${i}`}
                  className="bg-white border border-slate-200 rounded-2xl p-7 card-hover"
                >
                  <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                    <PIcon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scope */}
      <section className="py-24 lg:py-28 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-eyebrow">Scope of work</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              One SLA. End-to-end FM.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{t("hero.disclosure")}</p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
              {scope.map((s, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-blue-700 mt-0.5 shrink-0" />
                  <span className="text-slate-700 leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section className="py-24 lg:py-28 bg-white">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <div className="text-eyebrow text-blue-700">{t("audit.eyebrow")}</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Audit your facility free.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              A senior consultant walks your site, benchmarks your current FM spend and emails a
              60-point report within 48 hours.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-xl shadow-blue-900/5">
              <LeadForm pageSource={`industry-${slug}`} defaultService="facility-management" simple />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
